/** Prefijos de comprobante por NroTipoFormulario (ventas / tesorería cliente). */
export const TIPO_COMPROBANTE_PREFIJO: Record<string, string> = {
  '1': 'FC',
  '2': 'ND',
  '3': 'NC',
  '4': 'REC',
  '6': 'OP',
  '60': 'DEV',
  '80': 'NCI',
  '81': 'NDI',
};

/** Extrae NroTipoFormulario de textos como "6 - Orden Pago". */
export const parseNroTipoComprobante = (tipoComprobante?: string | null): string => {
  const txt = (tipoComprobante ?? '').toString().trim();
  const match = txt.match(/^(\d+)\s*[-–]\s*/);
  return match ? match[1] : '';
};

export const tipoToPrefijo = (tipo?: string | null): string => {
  const t = (tipo ?? '').toString().trim();
  const parsed = parseNroTipoComprobante(t) || t.replace(/^0+/, '') || t;
  return TIPO_COMPROBANTE_PREFIJO[parsed] || TIPO_COMPROBANTE_PREFIJO[t] || t || '-';
};

/** Devolución de venta (tipo 60 DEV). */
export const esDevolucionCobranzaCliente = (nroTipo: string, comprobanteTxt = ''): boolean => {
  const t = (nroTipo ?? '').toString().trim();
  if (t === '60') return true;
  return comprobanteTxt.toUpperCase().includes('DEVOLUCION');
};

/** Créditos aplicables en cobranzas (recibos, notas de crédito). */
export const esCreditoCobranzaCliente = (nroTipo: string, comprobanteTxt = ''): boolean => {
  const t = (nroTipo ?? '').toString().trim();
  if (t === '3' || t === '4' || t === '80') return true;
  const c = comprobanteTxt.toUpperCase();
  return c.includes('RECIBO') || c.includes('NOTA DE CR');
};

/** Créditos desde los que se puede aplicar saldo (recibo, NC). */
export const esCreditoAplicableDesdeCobranza = esCreditoCobranzaCliente;

/** NroTipoFormulario imputables al crear un recibo (FC, ND, devolución, NDI). */
const TIPOS_DEBITO_IMPUTABLES_RECIBO = new Set(['1', '2', '60', '81']);

/** Imputable en recibo exclusivamente por NroTipoFormulario. */
export const esDebitoImputableEnRecibo = (nroTipo?: string | null): boolean =>
  TIPOS_DEBITO_IMPUTABLES_RECIBO.has((nroTipo ?? '').toString().trim());

const nroTipoResuelto = (nroTipo: string, comprobanteTxt: string): string => {
  const t = (nroTipo ?? '').toString().trim();
  return parseNroTipoComprobante(t) || t || parseNroTipoComprobante(comprobanteTxt);
};

const nroTipoNormalizado = (nroTipo: string, comprobanteTxt: string): string => {
  const t = nroTipoResuelto(nroTipo, comprobanteTxt);
  return t.replace(/^0+/, '') || t;
};

/** Nota de crédito de venta (cuenta corriente cliente). Tipos 3 NC y 80 NCI; no incluye recibos (4). */
export const esNotaCreditoCliente = (nroTipo: string, comprobanteTxt = ''): boolean => {
  const t = nroTipoNormalizado(nroTipo, comprobanteTxt);
  if (t === '3' || t === '80') return true;
  const c = comprobanteTxt.toUpperCase();
  if (c.includes('NOTA DE CR')) return true;
  if (c.includes('NOTA') && c.includes('CRED')) return true;
  const s = comprobanteTxt.trim();
  return /^(NC|NCI)-/i.test(s) || /\bNC-/i.test(s);
};

/** Nota de crédito / crédito interno de compra (cuenta corriente proveedor). Tipos 3 NC y 80 NCI. */
export const esNotaCreditoProveedor = (nroTipo: string, comprobanteTxt = ''): boolean => {
  const t = nroTipoNormalizado(nroTipo, comprobanteTxt);
  if (t === '3' || t === '80') return true;
  const c = comprobanteTxt.toUpperCase();
  if (c.includes('NOTA DE CR')) return true;
  if (c.includes('NOTA') && c.includes('CRED')) return true;
  if (c.includes('CREDITO') && c.includes('INTERNO')) return true;
  const s = comprobanteTxt.trim();
  return /^(NC|NCI)-/i.test(s) || /\bNC-/i.test(s);
};

/** Créditos en cuenta corriente proveedor (orden de pago, nota de crédito, crédito interno). No se imputan al crear una OP. */
export const esCreditoPagoProveedor = (nroTipo: string, comprobanteTxt = ''): boolean => {
  if (esNotaCreditoProveedor(nroTipo, comprobanteTxt)) return true;
  const t = nroTipoNormalizado(nroTipo, comprobanteTxt);
  if (t === '6' || t === '80') return true;
  const c = comprobanteTxt.toUpperCase();
  return (
    (c.includes('ORDEN') && c.includes('PAGO')) ||
    c.includes('ORDENPAGO') ||
    (c.includes('CREDITO') && c.includes('INTERNO'))
  );
};

/** Débito interno (no imputable en cobranzas ni pagos). */
export const esDebitoInternoTesoreria = (nroTipo: string, comprobanteTxt = ''): boolean => {
  const t = nroTipoResuelto(nroTipo, comprobanteTxt);
  if (t === 'DI') return true;
  const c = comprobanteTxt.toUpperCase();
  return c.includes('DEBITO') && c.includes('INTERNO');
};

/** Débitos imputables al crear orden de pago (facturas, ND de compra y débito interno). */
export const esDebitoPagoProveedor = (nroTipo: string, comprobanteTxt = ''): boolean => {
  const t = nroTipoResuelto(nroTipo, comprobanteTxt);
  if (t === '1' || t === '2' || t === '81') return true;
  const c = comprobanteTxt.toUpperCase();
  if (c.includes('FACTURA') || c.includes(' FC')) return true;
  return (
    c.includes('NOTA DE DÉBITO') ||
    c.includes('NOTA DE DEBITO') ||
    (c.includes('NOTA') && c.includes('DEBITO') && !c.includes('CREDITO')) ||
    (c.includes('DEBITO') && c.includes('INTERNO'))
  );
};

/** Débitos de cuenta corriente cliente imputables en recibos (facturas, ND, devoluciones). */
export const esDebitoCobranzaCliente = (nroTipo: string, comprobanteTxt = ''): boolean => {
  const t = nroTipoResuelto(nroTipo, comprobanteTxt);
  if (t === '1' || t === '2' || t === '60' || t === '81') return true;
  const c = comprobanteTxt.toUpperCase();
  return (
    c.includes('FACTURA') ||
    c.includes('FC') ||
    c.includes('NOTA DE DÉBITO') ||
    c.includes('NOTA DE DEBITO') ||
    (c.includes('NOTA') && c.includes('DEBITO') && !c.includes('CREDITO')) ||
    c.includes('DEVOLUCION')
  );
};
